class UpsellProductPDP extends HTMLElement {
  constructor() {
    super();
    this._updateDebounce = null;
    this._alpineUnsub = null;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleCartChangeEvent = this.handleCartChangeEvent.bind(this);
  }

  connectedCallback() {
    this.loadRecommendations(this.dataset.productId);

    document.addEventListener('cart:updated', this.handleCartUpdate);
    document.addEventListener('theme:cart:updated', this.handleCartUpdate);
    window.addEventListener('shapes:modalcart:afteradditem', this.handleCartChangeEvent);
    window.addEventListener('shapes:modalcart:cartqtychange', this.handleCartChangeEvent);

    if (window.Alpine && window.Alpine.store && window.Alpine.store('cart_count')) {
      try {
        this._alpineUnsub = window.Alpine.store('cart_count').$subscribe(() => {
          clearTimeout(this._updateDebounce);
          this._updateDebounce = setTimeout(() => this.handleCartUpdate(), 120);
        });
      } catch (e) {}
    }
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.handleCartUpdate);
    document.removeEventListener('theme:cart:updated', this.handleCartUpdate);
    window.removeEventListener('shapes:modalcart:afteradditem', this.handleCartChangeEvent);
    window.removeEventListener('shapes:modalcart:cartqtychange', this.handleCartChangeEvent);

    if (this._alpineUnsub && typeof this._alpineUnsub === 'function') {
      try {
        this._alpineUnsub();
      } catch (e) {}
      this._alpineUnsub = null;
    }
  }

  async getCart() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async handleCartUpdate() {
    clearTimeout(this._updateDebounce);
    this._updateDebounce = setTimeout(async () => {
      const cart = await this.getCart();
      const newProductId =
        cart && cart.items && cart.items[0]
          ? cart.items[0].product_id || (cart.items[0].product && cart.items[0].product.id) || null
          : null;

      if (!newProductId) {
        this.innerHTML = '';
        return;
      }

      if (String(newProductId) !== String(this.dataset.productId) || !this.querySelector('.pdp-upsell-section')) {
        this.dataset.productId = newProductId;
        this.loadRecommendations(newProductId);
      }
    }, 120);
  }

  async handleCartChangeEvent(e) {
    try {
      if (e && e.detail && e.detail.response && e.detail.response.cart) {
        const cart = e.detail.response.cart;
        const newProductId =
          cart.items && cart.items[0]
            ? cart.items[0].product_id || (cart.items[0].product && cart.items[0].product.id) || null
            : null;

        if (!newProductId) {
          this.innerHTML = '';
          return;
        }

        if (String(newProductId) !== String(this.dataset.productId) || !this.querySelector('.pdp-upsell-section')) {
          this.dataset.productId = newProductId;
          this.loadRecommendations(newProductId);
        }

        return;
      }
    } catch (err) {
      console.error('Error handling cart change event:', err);
    }

    await this.handleCartUpdate();
  }

  loadRecommendations(productId) {
    if (!this.dataset.sectionId) return;

    if (!productId) {
      this.innerHTML = '';
      return;
    }

    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((res) => res.text())
      .then((htmlStr) => {
        const temp = document.createElement('div');
        temp.innerHTML = htmlStr;

        const section = temp.querySelector('[id^="shopify-section"]');
        const recommendations = section?.querySelector('.pdp-upsell-section');

        if (!recommendations) {
          console.warn('Upsell section not found');
          this.innerHTML = '';
          return;
        }

        this.innerHTML = recommendations.outerHTML;

        requestAnimationFrame(() => {
          this.initSlider();
          this.initQuickAdd();
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  initQuickAdd() {
    this.querySelectorAll('.upsell-add-to-cart').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        const id = btn.dataset.variantId;
        if (!id) return;

        try {
          const modalCart = theme.settings && theme.settings.cart_type === 'modal';

          if (modalCart) {
            const formData = new FormData();
            formData.append('id', id);
            formData.append('quantity', 1);
            formData.append('sections', 'cart-items,cart-footer,cart-item-count');
            formData.append('sections_url', window.location.pathname);

            const config = fetchConfigDefaults('javascript');
            config.body = formData;
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];

            const res = await fetch(`${theme.routes.cart_add_url}`, config);
            const data = await res.json();

            document.body.dispatchEvent(
              new CustomEvent('shapes:modalcart:afteradditem', {
                bubbles: true,
                detail: { response: data },
              }),
            );

            if (window.Alpine && window.Alpine.store('cart_count')) {
              try {
                const countSectionHTML = data.sections['cart-item-count'];
                window.Alpine.store('cart_count').count = parseInt(
                  parseDOMFromString(countSectionHTML).firstElementChild.innerText.trim(),
                  10,
                );
              } catch (e) {}
            }

            try {
              if (this.dataset && this.dataset.productId) this.loadRecommendations(this.dataset.productId);
            } catch (e) {}
          } else {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id, quantity: 1 }),
            });

            const cartResponse = await fetch('/cart.json');
            const cartData = await cartResponse.json();

            if (window.Alpine && window.Alpine.store('cart_count')) {
              window.Alpine.store('cart_count').count = cartData.item_count;
            }

            try {
              if (this.dataset && this.dataset.productId) this.loadRecommendations(this.dataset.productId);
            } catch (e) {}
          }
        } catch (error) {
          console.error('Error:', error);
          this.showErrorMessage('Error adding item to cart');
        }
      });
    });
  }

  initSlider() {
    const slider = this.querySelector('.upsell-slider');
    if (!slider) return;

    const inDrawer = !!this.closest('#right-drawer-slot');

    const config = {
      slidesPerView: inDrawer ? 1.2 : 'auto',
      spaceBetween: 8,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      pagination: {
        el: slider.querySelector('.swiper-pagination'),
        clickable: true,
      },
      scrollbar: {
        el: slider.querySelector('.swiper-scrollbar'),
        draggable: true,
      },
    };

    new Swiper(slider, config);
  }
}

customElements.define('upsell-product', UpsellProductPDP);

class FreeShippingBar extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
    this.freeShippingThreshold = parseInt(this.dataset.freeShippingThreshold) || 5000;
    this.freeProductThreshold = parseInt(this.dataset.freeProductThreshold) || 10000;
    this.cartTotal = parseInt(this.dataset.cartTotal) || 0;
    this.hasFreeShipping = this.dataset.hasFreeShipping === 'true';
    this.hasFreeGift = this.dataset.hasFreeGift === 'true';
    this.freeProductId = this.dataset.freeProductId;
    this.freeVariantId = this.dataset.freeVariantId;
    this.freeProductDiscountCode = this.dataset.freeProductDiscountCode || '';
    this.freeGiftKey = this.dataset.freeGiftKey;

    this._updateDebounce = null;
    this._isAddingFreeGift = false;
    this._isRemovingFreeGift = false;

    this.initialize();
  }

  async initialize() {
    this.cart = await this.getCart();
    this.updateDisplay();
    this.setupEventListeners();

    setTimeout(() => this.checkAndToggleFreeGift(), 120);
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return null;
    }
  }

  calculateProgress() {
    const maxThreshold = Math.max(this.freeShippingThreshold, this.freeProductThreshold);
    const cartTotal = this.getCartTotalExcludingFreeGift();
    const progress = (cartTotal / maxThreshold) * 100;
    return Math.min(Math.max(progress, 0), 100);
  }

  getCartTotalExcludingFreeGift() {
    if (!this.cart) return this.cartTotal;

    let total = this.cart.total_price;

    this.cart.items.forEach((item) => {
      if (item.properties && item.properties._is_free_gift === 'true') {
        total -= item.final_line_price;
      }
    });

    return total;
  }

  updateDisplay() {
    const cartTotal = this.getCartTotalExcludingFreeGift();
    const progress = this.calculateProgress();

    const progressBar = this.querySelector('.progress-bar-fill');
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
      progressBar.setAttribute('aria-valuenow', Math.round(progress));
    }

    this.updateMessage(cartTotal, progress);

    this.updateMilestones(cartTotal);
  }

  updateMessage(cartTotal, progress) {
    const container = this.querySelector('.shipping-message-container');
    if (!container) return;

    let messageHTML = '';
    const amountNeededForShipping = Math.max(0, this.freeShippingThreshold - cartTotal);
    const amountNeededForGift = Math.max(0, this.freeProductThreshold - cartTotal);

    if (this.freeProductId) {
      if (cartTotal < this.freeProductThreshold) {
        messageHTML = `
          <div class="shipping-message progress">
            <p class="shipping-message__copy tw-text-[14px] tw-text-text_color tw-font-normal tw-font-primary tw-leading-[1.35] tw-w-full tw-text-center tw-mb-[8px]">
              Spend <strong class="shipping-message__amount">${this.formatMoney(
                amountNeededForGift,
              )} more,</strong> for a Free Totel!
            </p>
          </div>
        `;
      } else if (cartTotal >= this.freeProductThreshold && cartTotal < this.freeShippingThreshold) {
        messageHTML = `
          <div class="shipping-message unlocked">
            <p class="shipping-message__copy tw-text-[14px] tw-text-text_color tw-font-normal tw-font-primary tw-leading-[1.35] tw-w-full tw-text-center tw-mb-[8px] tw-capitalize">
              <strong class="shipping-message__highlight">Free Tote Bag</strong> unlocked!<br class="shipping-message__br">Spend <strong class="shipping-message__amount">${this.formatMoney(
                amountNeededForShipping,
              )}</strong> more to unlock Free Shipping!
            </p>
          </div>
        `;
      } else {
        messageHTML = `
          <div class="shipping-message unlocked">
            <p class="shipping-message__copy tw-text-[14px] tw-text-text_color tw-font-normal tw-font-primary tw-leading-[1.35] tw-w-full tw-text-center tw-mb-[8px]">
              <strong class="shipping-message__highlight">Free Shipping</strong> & <strong class="shipping-message__highlight">Free Tote</strong> unlocked!
            </p>
          </div>
        `;
      }
    } else {
      if (cartTotal >= this.freeShippingThreshold) {
        messageHTML = `
          <div class="shipping-message unlocked">
            <p class="shipping-message__copy tw-text-[14px] tw-text-text_color tw-font-normal tw-font-primary tw-leading-[1.35] tw-w-full tw-text-center tw-mb-[8px]">
              <strong class="shipping-message__highlight">Free Shipping</strong> unlocked!
            </p>
          </div>
        `;
      } else {
        messageHTML = `
          <div class="shipping-message progress">
            <p class="shipping-message__copy tw-text-[14px] tw-text-text_color tw-font-normal tw-font-primary tw-leading-[1.35] tw-w-full tw-text-center tw-mb-[8px]">
              Spend <strong class="shipping-message__amount">${this.formatMoney(
                amountNeededForShipping,
              )} more</strong>, unlock <strong class="shipping-message__highlight">Free Shipping</strong>!
            </p>
          </div>
        `;
      }
    }

    const existingMsg = container.querySelector('.shipping-message');
    if (existingMsg) {
      existingMsg.outerHTML = messageHTML;
    } else {
      container.insertAdjacentHTML('afterbegin', messageHTML);
    }
  }

  updateMilestones(cartTotal) {
    const shippingIcon = this.querySelector('.shipping-milestone .milestone-icon');
    const giftIcon = this.querySelector('.gift-milestone .milestone-icon');

    if (shippingIcon) {
      const shippingUnlocked = cartTotal >= this.freeShippingThreshold;
      shippingIcon.classList.toggle('milestone--unlocked', shippingUnlocked);
      const svg = shippingIcon.querySelector('svg');
      if (svg) {
        svg.classList.toggle('milestone__svg--unlocked', shippingUnlocked);
        svg.classList.toggle('milestone__svg--muted', !shippingUnlocked);
      }
    }

    if (giftIcon) {
      const giftUnlocked = cartTotal >= this.freeProductThreshold;
      giftIcon.classList.toggle('milestone--unlocked', giftUnlocked);
      const svg = giftIcon.querySelector('svg');
      if (svg) {
        svg.classList.toggle('milestone__svg--unlocked', giftUnlocked);
        svg.classList.toggle('milestone__svg--muted', !giftUnlocked);
      }
    }

    const shippingDot = this.querySelector('.shipping-milestone .milestone-dots');
    const giftDot = this.querySelector('.gift-milestone .milestone-dots');

    if (shippingDot) {
      const shippingUnlocked = cartTotal >= this.freeShippingThreshold;
      shippingDot.classList.toggle('milestone-dot--unlocked', shippingUnlocked);
    }

    if (giftDot) {
      const giftUnlocked = cartTotal >= this.freeProductThreshold;
      giftDot.classList.toggle('milestone-dot--unlocked', giftUnlocked);
    }
  }

  formatMoney(cents) {
    const dollars = cents / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(dollars);
  }

  syncGiftStateFromCart() {
    this.hasFreeGift = false;
    this.freeGiftKey = null;

    if (!this.cart || !this.cart.items) return;

    for (const item of this.cart.items) {
      const propVal = item.properties && item.properties._is_free_gift;
      const propTruthy = propVal === 'true' || propVal === true || propVal === '1' || propVal === 1;

      const variantId = (item.variant && item.variant.id) || item.variant_id || item.id || null;
      const variantMatches =
        this.freeVariantId && variantId && parseInt(this.freeVariantId, 10) === parseInt(variantId, 10);

      if (propTruthy || variantMatches) {
        this.hasFreeGift = true;
        this.freeGiftKey = item.key;
        break;
      }
    }
  }

  async checkAndToggleFreeGift() {
    this.syncGiftStateFromCart();

    const cartTotal = this.getCartTotalExcludingFreeGift();

    if (this.freeProductId && this.freeVariantId && cartTotal >= this.freeProductThreshold) {
      if (!this.hasFreeGift && !this._isAddingFreeGift) {
        await this.addFreeGiftToCart();
        return;
      }
    }

    if (this.freeGiftKey && cartTotal < this.freeProductThreshold) {
      if (this.hasFreeGift && !this._isRemovingFreeGift) {
        await this.removeFreeGiftFromCart();
        return;
      }
    }

    this.updateDisplay();
  }

  setupEventListeners() {
    document.addEventListener('cart:updated', () => this.handleCartUpdate());

    document.addEventListener('theme:cart:updated', () => this.handleCartUpdate());

    window.addEventListener('shapes:modalcart:afteradditem', (e) => this.handleCartChangeEvent(e));
    window.addEventListener('shapes:modalcart:cartqtychange', (e) => this.handleCartChangeEvent(e));

    if (window.Alpine && window.Alpine.watch) {
      window.Alpine.watch(
        () => window.Alpine.store('cart_count').count,
        () => {
          clearTimeout(this._updateDebounce);
          this._updateDebounce = setTimeout(() => {
            this.handleCartUpdate();
          }, 100);
        },
      );
    }
  }

  async handleCartUpdate() {
    clearTimeout(this._updateDebounce);
    this._updateDebounce = setTimeout(async () => {
      this.cart = await this.getCart();
      this.updateDisplay();
      await this.checkAndToggleFreeGift();
    }, 120);
  }

  async handleCartChangeEvent(e) {
    try {
      if (e && e.detail && e.detail.response) {
        const resp = e.detail.response;

        if (resp.cart) {
          this.cart = resp.cart;
          this.updateDisplay();
          await this.checkAndToggleFreeGift();
          return;
        }
      }
    } catch (err) {
      console.error('Error handling cart event:', err);
    }

    clearTimeout(this._updateDebounce);
    this._updateDebounce = setTimeout(async () => {
      this.cart = await this.getCart();
      this.updateDisplay();
      await this.checkAndToggleFreeGift();
    }, 120);
  }

  async applyFreeGiftDiscountCode() {
    if (!this.freeProductDiscountCode) return;
    try {
      // Hits Shopify's discount-cookie endpoint without navigating away.
      // The redirect:'manual' makes fetch reject after the 302 — the cookie is set regardless.
      await fetch(`/discount/${encodeURIComponent(this.freeProductDiscountCode)}`, {
        method: 'GET',
        redirect: 'manual',
        credentials: 'same-origin',
      });
    } catch (e) {
      // Expected: opaque/redirect responses throw under redirect:'manual'.
    }
  }

  async addFreeGiftToCart() {
    if (!this.freeProductId || !this.freeVariantId) return;

    if (this._isAddingFreeGift) return;

    this.cart = await this.getCart();
    this.syncGiftStateFromCart();

    if (this.hasFreeGift) return;

    if (this.cart && this.cart.items) {
      for (const item of this.cart.items) {
        const variantId = (item.variant && item.variant.id) || item.variant_id || item.id || null;
        if (variantId && parseInt(variantId, 10) === parseInt(this.freeVariantId, 10)) {
          this.hasFreeGift = true;
          return;
        }
      }
    }

    this._isAddingFreeGift = true;

    try {
      const modalCart = theme && theme.settings && theme.settings.cart_type === 'modal';

      if (modalCart && theme.routes && theme.routes.cart_add_url) {
        const formData = new FormData();
        formData.append('id', this.freeVariantId);
        formData.append('quantity', 1);
        formData.append('properties[_is_free_gift]', 'true');
        formData.append('sections', 'cart-items,cart-footer,cart-item-count,cart-live-region');
        formData.append('sections_url', window.location.pathname);

        const config = fetchConfigDefaults('javascript');
        config.body = formData;
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const res = await fetch(`${theme.routes.cart_add_url}`, config);
        const data = await res.json();

        if (data.cart) this.cart = data.cart;

        await this.applyFreeGiftDiscountCode();

        document.body.dispatchEvent(
          new CustomEvent('shapes:modalcart:afteradditem', {
            bubbles: true,
            detail: { response: data },
          }),
        );

        try {
          if (window.Alpine && window.Alpine.store && window.Alpine.store('cart_count')) {
            const countSectionHTML = data.sections && data.sections['cart-item-count'];
            if (countSectionHTML) {
              window.Alpine.store('cart_count').count = parseInt(
                parseDOMFromString(countSectionHTML).firstElementChild.innerText.trim(),
                10,
              );
            }
          }
        } catch (e) {}

        await this.handleCartUpdate();
      } else {
        const formData = new FormData();
        formData.append('id', this.freeVariantId);
        formData.append('quantity', 1);
        formData.append('properties[_is_free_gift]', 'true');

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData,
        });

        if (response.ok) {
          await this.applyFreeGiftDiscountCode();
          this.cart = await this.getCart();
          await this.handleCartUpdate();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      }
    } catch (error) {
      console.error('Error adding free gift:', error);
    } finally {
      this._isAddingFreeGift = false;
    }
  }

  async removeFreeGiftFromCart() {
    this.cart = await this.getCart();
    this.syncGiftStateFromCart();

    if (!this.freeGiftKey) return;

    if (this._isRemovingFreeGift) return;

    this._isRemovingFreeGift = true;

    try {
      const modalCart = theme && theme.settings && theme.settings.cart_type === 'modal';

      if (modalCart && theme.routes && theme.routes.cart_change_url) {
        const config = fetchConfigDefaults('javascript');
        config.body = JSON.stringify({
          id: this.freeGiftKey,
          quantity: 0,
          sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
          sections_url: window.location.pathname,
        });

        const res = await fetch(`${theme.routes.cart_change_url}`, config);
        const data = await res.json();

        if (data.cart) this.cart = data.cart;

        document.body.dispatchEvent(
          new CustomEvent('shapes:modalcart:cartqtychange', {
            bubbles: true,
            detail: { response: data },
          }),
        );

        try {
          if (window.Alpine && window.Alpine.store && window.Alpine.store('cart_count')) {
            const countSectionHTML = data.sections && data.sections['cart-item-count'];
            if (countSectionHTML) {
              window.Alpine.store('cart_count').count = parseInt(
                parseDOMFromString(countSectionHTML).firstElementChild.innerText.trim(),
                10,
              );
            }
          }
        } catch (e) {}

        await this.handleCartUpdate();
      } else {
        const updates = { [this.freeGiftKey]: 0 };

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        });

        if (response.ok) {
          console.log('Free gift removed from cart');
          await this.handleCartUpdate();
          document.dispatchEvent(new CustomEvent('cart:updated'));
        }
      }
    } catch (error) {
      console.error('Error removing free gift:', error);
    } finally {
      this._isRemovingFreeGift = false;
    }
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);

document.addEventListener('DOMContentLoaded', () => {
  const marquees = document.querySelectorAll('[data-marquee-container]');

  marquees.forEach((container) => {
    const track = container.querySelector('[data-marquee-track]');
    if (!track) return;

    let direction = -1;
    let position = 0;
    let speed = 60;

    let addLimit = 3;
    for (let i = 0; i < addLimit; i++) {
      track.innerHTML += track.innerHTML;
    }

    const trackWidth = track.scrollWidth / 2;

    track.style.willChange = 'transform';
    track.style.transform = `translate3d(0,0,0)`;

    function animate(lastTime) {
      return function frame(time) {
        if (!lastTime) lastTime = time;
        const delta = (time - lastTime) / 1000;
        lastTime = time;

        position += direction * speed * delta;

        if (direction === -1) {
          if (position <= -trackWidth) position += trackWidth;
        } else {
          if (position >= 0) position -= trackWidth;
        }

        track.style.transform = `translate3d(${position}px, 0, 0)`;
        requestAnimationFrame(frame);
      };
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(animate());
    });

    // subscriptions for pausing on hover

    const intervalId = window.setInterval(function () {
      const subscriptionElm = document.querySelector('.skio-plan-picker')?.closest('.shopify-app-block');
      const buyButtonElm = document.querySelector('.buy-buttons-main');
      if (subscriptionElm && buyButtonElm) {
        const subscriptionContentElm = subscriptionElm.querySelector('.skio-group-content') || subscriptionElm;
        const contentElm =
          subscriptionContentElm.querySelector('ul') ||
          subscriptionContentElm.appendChild(document.createElement('ul'));
        const subscriptionBenefits = ['Save 10% on every order', 'Modify or cancel any time.'];

        subscriptionBenefits.forEach((text) => {
          const exists = Array.from(contentElm.querySelectorAll('li')).some((item) => item.textContent.trim() === text);

          if (!exists) {
            const li = document.createElement('li');
            li.textContent = text;
            contentElm.appendChild(li);
          }
        });

        const selectedVariant = subscriptionElm.querySelector('.skio-group-container.skio-group-container--selected');

        if (selectedVariant) {
          const priceElm = selectedVariant.querySelector('.skio-price span').textContent;
          const buyButtonPriceElm = buyButtonElm.querySelector('.button-product-price');
          if (buyButtonPriceElm) {
            const formattedPrice = priceElm.replace(/(\.00)(?!.*\d)/, '');
            buyButtonPriceElm.textContent = formattedPrice;
          }
        }

        // Price set in atc button price
        subscriptionElm.querySelectorAll('.skio-group-input').forEach((item) => {
          item.addEventListener('change', (input) => {
            const priceElm = input.target
              .closest('.skio-group-container')
              .querySelector('.skio-price span').textContent;
            const buyButtonPriceElm = buyButtonElm.querySelector('.button-product-price');
            if (buyButtonPriceElm) {
              const formattedPrice = priceElm.replace(/(\.00)(?!.*\d)/, '');
              buyButtonPriceElm.textContent = formattedPrice;
            }
          });
        });

        buyButtonElm.parentNode.insertBefore(subscriptionElm, buyButtonElm);
        clearInterval(intervalId);
      }
    }, 1000);

    setTimeout(() => {
      clearInterval(intervalId);
    }, 10000);
  });

  const marqueeTextMain = document.querySelector('.marquee-text-main.desktop-megamenu-marquee');
  const cloneMarquee = document.querySelector('.sidebar-marquee');

  if (marqueeTextMain && cloneMarquee) {
    cloneMarquee.innerHTML = marqueeTextMain.outerHTML;
  }
});

const announcementBar = document.querySelector('.announcement-bar');

if (announcementBar) {
  function updateAnnouncementState() {
    const announcementHeight = announcementBar.offsetHeight;

    if (window.scrollY < announcementHeight) {
      document.documentElement.classList.add('announcementbar-visible');
    } else {
      document.documentElement.classList.remove('announcementbar-visible');
    }
  }

  updateAnnouncementState();
  window.addEventListener('scroll', updateAnnouncementState);
}

document.addEventListener('DOMContentLoaded', () => {
  const priceContainer = document.querySelector('.skio-plan-picker .skio-group-container .skio-price');
  const oneTimePriceEl = document.querySelector('.skio-plan-picker .skio-group-container [skio-onetime-price]');

  if (!priceContainer || !oneTimePriceEl) return;

  let comparePriceEl = priceContainer.querySelector('.skio-plan-picker .skio-group-container .skio-compare-price');
  if (!comparePriceEl) {
    comparePriceEl = document.createElement('span');
    comparePriceEl.classList.add('skio-compare-price');
    priceContainer.appendChild(comparePriceEl);
  }

  comparePriceEl.textContent = oneTimePriceEl.textContent;
  const observer = new MutationObserver(() => {
    comparePriceEl.textContent = oneTimePriceEl.textContent;
  });

  observer.observe(oneTimePriceEl, {
    childList: true,
    subtree: true,
    characterData: true,
  });
});
 